import { createFileRoute, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { z } from "zod";

import { Footer, Nav } from "../components/lidoo/chrome";
import { ClubSearch } from "../components/lidoo/club-search";
import { ClubsView } from "../components/lidoo/clubs-view";
import { searchClubs, type Club, type SearchResult } from "../lib/api/lidoo.functions";
import { haversineKm } from "../lib/geo";
import { facilityLabel } from "../lib/facilities";
import { countryName, fill, regionName, useI18n } from "../lib/i18n";
import { hreflangLinks } from "../lib/seo";

const searchSchema = z.object({
  q: z.string().optional(),
  region: z.string().optional(),
  near: z.string().optional(),
  // Comma-separated facility filters, e.g. ?f=restaurant,pool
  f: z.string().optional(),
  // Only clubs that publish a rate.
  priced: z.boolean().optional(),
  // 1-based. In the URL so a page of results can be linked and indexed.
  page: z.number().int().min(1).max(200).optional(),
  sort: z.enum(["pick", "rating", "price_asc", "price_desc", "name"]).optional(),
});

const SORT_KEYS: [NonNullable<z.infer<typeof searchSchema>["sort"]>, string][] = [
  ["pick", "sort_relevance"],
  ["rating", "sort_rating"],
  ["price_asc", "sort_price_asc"],
  ["price_desc", "sort_price_desc"],
  ["name", "sort_name"],
];

// Numbered pages with an ellipsis, the pattern every travel site uses: it makes
// the size of the catalogue visible (our strongest argument at 827 clubs) and
// gives each page a link a crawler can follow. A prev/next pair over 35 pages
// tells a visitor nothing about where they are.
function pageWindow(page: number, pages: number): (number | "gap")[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out: (number | "gap")[] = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pages - 1, page + 1);
  if (from > 2) out.push("gap");
  for (let i = from; i <= to; i++) out.push(i);
  if (to < pages - 1) out.push("gap");
  out.push(pages);
  return out;
}

// "lat,lng" from the URL, as the home page's "near me" button writes it. Sent
// to the server so the whole catalogue is ordered by distance before paging,
// not just the page that happened to come back.
function parseNear(raw: string | undefined): { lat: number; lng: number } | undefined {
  if (!raw) return undefined;
  const [lat, lng] = raw.split(",").map(Number);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return undefined;
  return { lat, lng };
}

export const Route = createFileRoute("/clubs")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) =>
    await searchClubs({
      data: {
        q: deps.q,
        region: deps.region,
        facilities: deps.f ? deps.f.split(",").filter(Boolean) : undefined,
        priced: deps.priced,
        page: deps.page,
        near: parseNear(deps.near),
        sort: deps.sort,
      },
    }),
  head: () => ({
    meta: [{ title: "Beach clubs | sunbedly" }],
    links: hreflangLinks("/clubs"),
  }),
  component: ClubsPage,
});

// Reads the router rather than a fixed Route object, so the /nl/clubs,
// /it/clubs ... routes render the exact same page with their own loader data
// instead of silently falling back to the English route's.
export function ClubsPage() {
  const result = useRouterState({
    select: (s) =>
      (s.matches[s.matches.length - 1]?.loaderData as SearchResult | undefined) ?? {
        clubs: [], total: 0, page: 1, pages: 1, facets: [], pricedCount: 0, regions: [],
      },
  });
  const search = useRouterState({
    select: (s) => (s.matches[s.matches.length - 1]?.search ?? {}) as z.infer<typeof searchSchema>,
  });
  // The shared page is mounted under both /clubs and /$locale/clubs, so the
  // navigate helper cannot be typed against one of them. The search shape is
  // still checked: `patch` only accepts keys of the schema.
  const rawNavigate = useNavigate();
  const patch = (next: Partial<z.infer<typeof searchSchema>>) => {
    void (rawNavigate as (opts: unknown) => void)({
      // Any change to a filter sends you back to page one, because staying on
      // page 7 of a result set that now has 3 pages is how you get an empty
      // screen and no idea why.
      search: { ...search, page: undefined, ...next },
      replace: true,
    });
  };
  const { t } = useI18n();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const near = useMemo(() => {
    if (!search.near) return null;
    const [lat, lng] = search.near.split(",").map(Number);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }, [search.near]);

  // Countries in the order the server sorted them, each with its own regions.
  const byCountry = useMemo(() => {
    const groups = new Map<string, typeof result.regions>();
    for (const r of result.regions) {
      const list = groups.get(r.country);
      if (list) list.push(r);
      else groups.set(r.country, [r]);
    }
    // Sorted on the translated name, not the English one the server sorted by:
    // alphabetical in English reads as random once it says Kroatie, Cyprus,
    // Frankrijk.
    return [...groups.entries()].sort((a, b) =>
      countryName(a[0], t).localeCompare(countryName(b[0], t)),
    );
  }, [result.regions, t]);

  // The whole catalogue, not result.total: with a region selected that is the
  // count of the region you are already in, so the button to leave it would
  // promise fewer clubs than staying.
  const allRegionsCount = useMemo(
    () => result.regions.reduce((sum, r) => sum + r.count, 0),
    [result.regions],
  );

  const activeRegion = useMemo(
    () => result.regions.find((r) => r.key === search.region) ?? null,
    [result.regions, search.region],
  );

  const facilityCounts = result.facets;
  const activeFacilities = useMemo(
    () => (search.f ? search.f.split(",").filter(Boolean) : []),
    [search.f],
  );

  // Shown on the folded button so a visitor knows something is narrowing their
  // results even when the chips are out of sight.
  const activeFilterCount =
    (search.region ? 1 : 0) + (search.priced ? 1 : 0) + activeFacilities.length;

  // Distance is the one thing still worked out in the browser: it depends on
  // where the visitor is, which the server has no business knowing.
  const filtered = useMemo(() => {
    const list: (Club & { distanceKm?: number })[] = result.clubs;
    if (!near) return list;
    return list
      .map((c) =>
        c.lat != null && c.lng != null
          ? { ...c, distanceKm: haversineKm(near.lat, near.lng, c.lat, c.lng) }
          : { ...c, distanceKm: undefined },
      )
      .sort((a, b) => (a.distanceKm ?? 1e9) - (b.distanceKm ?? 1e9));
  }, [result.clubs, near]);

  const setRegion = (region?: string) => {
    // Facility facets are region-specific, so changing coast clears them
    // rather than leaving a filter active that matches nothing here.
    patch({ region, f: undefined });
  };

  const toggleFacility = (f: string) => {
    const next = activeFacilities.includes(f)
      ? activeFacilities.filter((x) => x !== f)
      : [...activeFacilities, f];
    patch({ f: next.length ? next.join(",") : undefined });
  };

  const togglePriced = () => {
    patch({ priced: search.priced ? undefined : true });
  };

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <Nav />
      <main className="mx-auto max-w-6xl px-5 pb-20">
        <div className="border-b border-[#1C2430]/15 pb-6 pt-10">
          <h1 className="li-serif text-3xl font-normal  text-[#1C2430] md:text-5xl">
            {t("clubs_title")}
          </h1>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <p className="li-mono text-[12px] text-[#5B6472]">
              {result.total === 1
                ? t("results_count_one")
                : fill(t("results_count"), { n: String(result.total) })}
            </p>
            {/* Distance ordering is applied in the browser and would silently
                override a server sort, so the control hides while "near me"
                is active rather than lying about what it does. */}
            {!near ? (
              <label className="flex items-center gap-2">
                <span className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
                  {t("sort_label")}
                </span>
                <select
                  value={search.sort ?? "pick"}
                  onChange={(e) =>
                    patch({ sort: e.target.value === "pick" ? undefined : (e.target.value as never) })
                  }
                  className="li-mono h-9 rounded-lg border border-[#1C2430]/20 bg-white/70 px-2 text-[12px] text-[#1C2430] focus:border-[#2C4FD6] focus:outline-none"
                >
                  {SORT_KEYS.map(([value, key]) => (
                    <option key={value} value={value}>
                      {t(key)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        </div>

        <div className="mt-6">
          <ClubSearch
            value={search.q ?? ""}
            onSubmit={(next) => patch({ q: next || undefined })}
            onPickRegion={(regionKey) => patch({ region: regionKey, q: undefined, f: undefined })}
          />
        </div>

        {/* One control instead of thirty-five chips.
            Grouped by country, so the list reads as a place to go rather than
            an alphabet, and it filters here rather than navigating away, which
            is what the facility chips beside it do. The coasts themselves stay
            crawlable: they are linked from the footer on every page, from the
            home page, and from the breadcrumb once one is chosen. */}
        <div className="mt-5 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="li-mono text-[10px] uppercase tracking-widest text-[#5B6472]">
              {t("dest_label")}
            </span>
            <select
              value={search.region ?? ""}
              onChange={(e) => setRegion(e.target.value || undefined)}
              className="li-display h-11 min-w-[16rem] rounded-lg border border-[#1C2430]/20 bg-white/70 px-3 text-sm text-[#1C2430] focus:border-[#2C4FD6] focus:outline-none"
            >
              <option value="">
                {t("dest_all")} ({allRegionsCount})
              </option>
              {byCountry.map(([country, regions]) => (
                <optgroup key={country} label={countryName(country, t)}>
                  {regions.map((r) => (
                    <option key={r.key} value={r.key}>
                      {regionName(r.key, r.label, t)} ({r.count})
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          {/* The coast has a page of its own with a write-up and its themes;
              once you have picked one, offer it rather than hiding it. */}
          {activeRegion ? (
            <Link
              to="/region/$key"
              params={{ key: activeRegion.key }}
              className="li-mono pb-3 text-[12px] text-[#2C4FD6] underline underline-offset-4"
            >
              {fill(t("dest_guide"), { place: regionName(activeRegion.key, activeRegion.label, t) })} →
            </Link>
          ) : null}
        </div>

        {/* On a phone the filters start folded away: 21 chips before the first
            club is a screen and a half of choosing how to choose. On a desktop
            there is room, so they stay open. */}
        <div className="mt-5 sm:hidden">
          <button
            type="button"
            onClick={() => setFiltersOpen(!filtersOpen)}
            aria-expanded={filtersOpen}
            className="li-mono flex h-11 w-full items-center justify-between rounded-lg border border-[#1C2430]/20 px-4 text-[12px] text-[#1C2430]"
          >
            <span>
              {t("filters_show")}
              {activeFilterCount > 0
                ? ` · ${fill(t("filters_active"), { n: String(activeFilterCount) })}`
                : ""}
            </span>
            <span aria-hidden="true">{filtersOpen ? "\u2013" : "+"}</span>
          </button>
        </div>

        <div className={`${filtersOpen ? "block" : "hidden"} sm:block`}>
        {/* Facets. Each carries its count, so you can see what a filter will
            give you before you spend a click on it. */}
        {facilityCounts.length > 0 ? (
          <div className="mt-5 border-t border-[#1C2430]/10 pt-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="li-mono mr-1 text-[11px] uppercase tracking-widest text-[#5B6472]">
                {t("filters_title")}
              </span>
              <button
                type="button"
                onClick={togglePriced}
                className={`li-mono rounded-full border px-3 py-1.5 text-[11px] transition-colors ${
                  search.priced
                    ? "border-[#2C4FD6] bg-[#2C4FD6] text-[#F2EFE9]"
                    : "border-[#1C2430]/20 text-[#1C2430] hover:border-[#2C4FD6]"
                }`}
              >
                {t("filter_priced")} ({result.pricedCount})
              </button>
              {facilityCounts.map(([f, n]) => {
                const on = activeFacilities.includes(f);
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => toggleFacility(f)}
                    className={`li-mono rounded-full border px-3 py-1.5 text-[11px] transition-colors ${
                      on
                        ? "border-[#2C4FD6] bg-[#2C4FD6] text-[#F2EFE9]"
                        : "border-[#1C2430]/20 text-[#1C2430] hover:border-[#2C4FD6]"
                    }`}
                  >
                    {facilityLabel(f, t)} ({n})
                  </button>
                );
              })}
              {activeFacilities.length > 0 || search.priced ? (
                <button
                  type="button"
                  onClick={() =>
                    patch({ f: undefined, priced: undefined })
                  }
                  className="li-mono px-2 py-1.5 text-[11px] text-[#5B6472] underline underline-offset-4 hover:text-[#1C2430]"
                >
                  {t("filters_clear")}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        </div>

        {filtered.length === 0 ? (
          /* An empty result should tell you what to do next, not just that
             nothing matched. */
          <div className="mt-16 rounded-2xl border border-dashed border-[#1C2430]/25 p-12 text-center">
            <p className="li-serif text-2xl text-[#1C2430]">{t("cl_none_t")}</p>
            <p className="mx-auto mt-2 max-w-[42ch] text-sm leading-relaxed text-[#5B6472]">
              {t("cl_none_d")}
            </p>
            <button
              type="button"
              onClick={() => patch({ q: undefined, f: undefined, priced: undefined, region: undefined })}
              className="li-mono mt-6 inline-block rounded-full border border-[#1C2430]/25 px-5 py-2.5 text-[12px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6]"
            >
              {t("cl_clear_all")}
            </button>
          </div>
        ) : (
          <>
            {/* What is currently narrowing the list, and a way out of each
                step. Without it the only feedback that a filter is on is a
                smaller number, and on a phone the chips are folded away. */}
            {activeFilterCount > 0 ? (
              <div className="mt-6 flex flex-wrap items-center gap-2">
                {activeRegion ? (
                  <button
                    type="button"
                    onClick={() => setRegion(undefined)}
                    className="li-mono inline-flex min-h-8 items-center gap-1.5 rounded-full bg-[#1C2430] px-3 py-1 text-[11px] text-[#F2EFE9]"
                  >
                    {regionName(activeRegion.key, activeRegion.label, t)}
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{t("filters_clear")}</span>
                  </button>
                ) : null}
                {search.priced ? (
                  <button
                    type="button"
                    onClick={togglePriced}
                    className="li-mono inline-flex min-h-8 items-center gap-1.5 rounded-full bg-[#1C2430] px-3 py-1 text-[11px] text-[#F2EFE9]"
                  >
                    {t("filter_priced")}
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{t("filters_clear")}</span>
                  </button>
                ) : null}
                {activeFacilities.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => toggleFacility(f)}
                    className="li-mono inline-flex min-h-8 items-center gap-1.5 rounded-full bg-[#1C2430] px-3 py-1 text-[11px] text-[#F2EFE9]"
                  >
                    {facilityLabel(f, t)}
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{t("filters_clear")}</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => patch({ region: undefined, f: undefined, priced: undefined })}
                  className="li-mono px-2 py-1 text-[11px] text-[#5B6472] underline underline-offset-4 hover:text-[#1C2430]"
                >
                  {t("filters_clear")}
                </button>
              </div>
            ) : null}

            <div className="mt-8">
              <ClubsView clubs={filtered} />
            </div>

            {/* Real links rather than buttons: a page of results should be
                shareable, bookmarkable and crawlable. */}
            {result.pages > 1 ? (
              <nav
                aria-label="Pagination"
                className="mt-12 flex items-center justify-between gap-4 border-t border-[#1C2430]/15 pt-6"
              >
                <button
                  type="button"
                  disabled={result.page <= 1}
                  onClick={() => patch({ page: result.page - 1 === 1 ? undefined : result.page - 1 })}
                  className="li-mono rounded-full border border-[#1C2430]/25 px-5 py-2.5 text-[12px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6] disabled:pointer-events-none disabled:opacity-35"
                >
                  ← {t("pg_prev")}
                </button>
                <div className="flex items-center gap-1">
                  {pageWindow(result.page, result.pages).map((p, i) =>
                    p === "gap" ? (
                      <span key={`gap${i}`} className="li-mono px-1 text-[12px] text-[#5B6472]">
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        aria-current={p === result.page ? "page" : undefined}
                        onClick={() => patch({ page: p === 1 ? undefined : p })}
                        className={`li-mono h-9 min-w-9 rounded-md px-2 text-[12px] [font-variant-numeric:tabular-nums] transition-colors ${
                          p === result.page
                            ? "bg-[#1C2430] text-[#F2EFE9]"
                            : "text-[#1C2430] hover:bg-[#1C2430]/8"
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                </div>
                <button
                  type="button"
                  disabled={result.page >= result.pages}
                  onClick={() => patch({ page: result.page + 1 })}
                  className="li-mono rounded-full border border-[#1C2430]/25 px-5 py-2.5 text-[12px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6] disabled:pointer-events-none disabled:opacity-35"
                >
                  {t("pg_next")} →
                </button>
              </nav>
            ) : null}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
